'use client';
import { useAppSettings } from '@bells/core';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import LocationSearch, {
  LocationSearchChangeEvent,
} from '../../components/location-search';

import { tags } from '@bells/iso/data/countries';
import type { FoundLocationReturn } from '@bells/schema/models/mapbox';
import { useMemo } from 'react';
import { useFilter } from './filter-provider';
import { AttendantSearchSortBy } from '@bells/schema/models/attendant';

const isValidDateString = (dateString: string) => {
  const date = new Date(dateString);
  return !isNaN(date.getTime());
};

const isValidNumberString = (numString: string) => {
  if (typeof numString !== 'string' || numString.trim() === '') return false;
  const num = Number(numString);
  return !isNaN(num);
};

export default function AttendantSearch({
  initSearchParams,
  location,
  onlyMobile,
  allowLocationChange = true,
}: {
  initSearchParams?: { [key: string]: string };
  location?: {
    lng: number;
    lat: number;
    name?: string;
    country?: (typeof tags)[number];
  };
  onlyMobile?: boolean;
  allowLocationChange?: boolean;
}) {
  const queryParams = useSearchParams();
  const pathName = usePathname();
  const { originUrl } = useAppSettings();
  const router = useRouter();
  const { setFilters } = useFilter();
  const onChange = (ev: LocationSearchChangeEvent) => {
    const params = new URLSearchParams(queryParams.toString());

    if (ev.duration && ev.startDate) {
      params.set('start', ev.startDate.toISOString());
      params.set('duration', ev.duration.toString());
      params.set('page', '1');
    }

    const newLocation = ev.location;
    if (newLocation) {
      const url = new URL(newLocation.link, originUrl);
      params.set('lng', newLocation.lng.toString());
      params.set('lat', newLocation.lat.toString());
      params.set('country', newLocation.country);
      params.set('page', '1');
      params.set('name', newLocation.name);
      params.set('sortBy', AttendantSearchSortBy.DISTANCE_ASC);

      if (allowLocationChange) {
        const routingUrl = url.pathname + '?' + params.toString();
        if (pathName != url.pathname) {
          router.replace(routingUrl);
          return;
        }
      } else {
        const routingUrl = pathName + '?' + params.toString();
        router.replace(routingUrl);
      }
    }

    setFilters((prev) => {
      return newLocation
        ? {
            ...prev,
            lng: newLocation.lng,
            lat: newLocation.lat,
            country: newLocation.country as (typeof tags)[number],
            name: newLocation.name,
            duration: ev.duration,
            sortBy: AttendantSearchSortBy.DISTANCE_ASC,
            start: ev.startDate?.toISOString(),
            page: 1,
          }
        : {
            ...prev,
            duration: ev.duration,
            start: ev.startDate?.toISOString(),
            page: 1,
          };
    });
  };

  const initData = useMemo(() => {
    if (!initSearchParams) return {};

    let initLocation: FoundLocationReturn | undefined = undefined;
    const cityName =
      initSearchParams['name'] || initSearchParams['city'] || location?.name;
    const country = initSearchParams['country'] || location?.country;
    const lat = initSearchParams['lat'] || location?.lat;
    const lng = initSearchParams['lng'] || location?.lng;
    if (cityName && lat && lng && country) {
      initLocation = {
        name: cityName,
        lng: Number(lng),
        lat: Number(lat),
        country: country as (typeof tags)[number],
        link: pathName,
      };
    }

    return {
      initLocation,
      initDate:
        initSearchParams['start'] !== undefined &&
        isValidDateString(initSearchParams['start'])
          ? new Date(initSearchParams['start'])
          : undefined,

      initDuration:
        initSearchParams['duration'] !== undefined &&
        isValidNumberString(initSearchParams['duration'])
          ? Number(initSearchParams['duration'])
          : undefined,
    };
  }, [initSearchParams]);

  return (
    <LocationSearch
      allowLocationChange={allowLocationChange}
      onlyMobile={onlyMobile}
      initLocation={initData.initLocation}
      initDate={initData.initDate}
      initDuration={initData.initDuration}
      onChange={onChange}
    ></LocationSearch>
  );
}
